import { useEffect, useMemo, useState } from "react";
import { getLocalId } from "easy-analytics/client";
import { api, handleError } from "../../services/api";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";

import { Footer, Header } from "../../Layout";

import { useRouter } from "easy-page-router/react";
import { useHead } from "../../services/common";
import { plural, useLang } from "../../services/lang";
import { ThemeSelect, ThemeSwitch } from "../../components/Theme";
import { LangSwitch } from "../../components/Lang";
import { Checkbox, CheckboxPublic, Input } from "../../components/Form";
import { Loading, Page, When as WhenShow } from "../../components";

import { DateDay, DateTime, When, WhenOption } from "../../../types";

const emptyWhen: When = {
    _id: "",
    userId: "",
    changed: "",
    lang: "en",
    theme: "",
    title: "",
    description: "",
    isMaybeOption: true,
    where: "",
    public: false,
    users: {},
    options: {},
}


export default function WhenForm() {
    const { push } = useRouter();
    const { lang, dict } = useLang();

    useHead({
        title: dict.seo.whenForm.title,
        description: dict.seo.whenForm.description,
    });

    const [when, setWhen] = useState(emptyWhen);
    const [message, setMessage] = useState("");
    const [isSending, setIsSending] = useState(false);

    const [options, setOptions] = useState<string[]>([]);

    function handleSelect(date: DateDay) {
        if (options.includes(date))
            handleRemove(date);
        else
            setOptions([...options, date].sort());
    }
    function handleRemove(date: DateDay) {
        setOptions(options.filter(o => o !== date));
    }


    async function handleSave() {
        if (!when.title) {
            return setMessage(dict.whenFormMissingTitle);
        }

        const clearOptions = options.filter(o => o.length > 0);
        if (clearOptions.length < 2) {
            return setMessage(dict.whenFormMissingOptions);
        }
        const whenOptions: Record<string, WhenOption> = {};
        clearOptions.forEach(option => whenOptions[option] = { description: "", users: {} });

        setIsSending(true);
        setMessage("");

        const [id, error] = await api.addWhen({
            ...when,
            options: whenOptions,
            userId: getLocalId(),
            lang,
        });

        setIsSending(false);

        if (!id) {
            handleError(error);
            setMessage(dict.whenFormError);
        } else {
            setMessage(dict.whenFormSuccess);
            push(`/${dict.urlWhen}/${id}`);
        }
    }

    return <Page fixed={<>
        <Header title={dict.when} link={`/${dict.urlWhen}`}>
            <LangSwitch />
            <ThemeSwitch />
        </Header>
        <Footer />
    </>}>

        <h1>{dict.newWhen}</h1>

        <div className="form">
            <label>{dict.theme}</label>
            <ThemeSelect
                value={when.theme}
                onChange={theme => setWhen({ ...when, theme })}
            />

            <Input autoFocus label={dict.whenFormTitle} field="title" maxLength={100} required value={when} setValue={setWhen} />
            <Input label={dict.whenFormDescription} field="description" maxLength={500} type="textarea" value={when} setValue={setWhen} />

            <label onClick={() => setWhen({ ...when, isMaybeOption: !when.isMaybeOption })}>
                <Checkbox value={when.isMaybeOption} />
                {dict.whenFormIsMaybeOption}
            </label>

            <Input label={dict.whenFormWhere} field="where" maxLength={50} value={when} setValue={setWhen} />

            {/* TODO <label>
                <input type="checkbox" />
                Chci zvolit čas (ne jen dny)
            </label>*/}

            <div className="calendar-select">
                <div className="select">
                    <label>{dict.whenSelectDates}</label>
                    {/* TODO <p className="caption">Jak to funguje, když jich chceš víc, klikni 2x</p> */}
                    <div>
                        <Calendar selected={options} onSelect={handleSelect} />
                    </div>

                </div>
                <div className="options">
                    <label>{dict.whenSelectedDates}</label>
                    {options.length === 0 && <p className="info">{dict.whenNoSelectedDatesInfo}</p>}
                    {options.map((option, i) => <div key={i} style={{ paddingLeft: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <WhenShow date={option} />
                        <a style={{ fontSize: "20px", }} onClick={() => handleRemove(option)}>✘</a>
                    </div>)}
                </div>
            </div>

            <CheckboxPublic
                value={when}
                onChange={setWhen}
                label={dict.pollFormPublic}
                captionPublic={dict.pollFormPublicCaption}
                captionPrivate={dict.pollFormPrivateCaption}
            />

            {message && <p className="alert">{message}</p>}

            {isSending && <Loading />}
            {!isSending && <button onClick={handleSave}>{dict.whenCreate}</button>}
        </div>

    </Page>;
}

type CalendarDay = {
    isInMonth: boolean,
    date: DateDay,
    dayNumber: number,
    isSelected: boolean,
}

function Calendar({ selected = [], onSelect }: { selected?: DateDay[], onSelect: (date: DateDay) => void }) {
    const { lang, dict } = useLang();
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    const [month, setMonth] = useState(() => getDateDay());

    const days = useMemo(() => {
        const dateMonth = new Date(month);

        const firstDay = new Date(dateMonth.getFullYear(), dateMonth.getMonth(), 1);
        let firstDayWeekDay = firstDay.getDay() - dict.firstDayOfWeek;
        if (firstDayWeekDay < 0) firstDayWeekDay += 7;
        firstDay.setDate(firstDay.getDate() - firstDayWeekDay);

        const lastDay = new Date(dateMonth.getFullYear(), dateMonth.getMonth() + 1, 0);
        let lastDayWeekDay = lastDay.getDay() - dict.firstDayOfWeek;
        if (lastDayWeekDay < 0) lastDayWeekDay += 7;
        lastDay.setDate(lastDay.getDate() + (6 - lastDayWeekDay));

        const days: CalendarDay[] = [];
        const selectedDays = selected.map(getDateDay);

        do {
            days.push({
                isInMonth: firstDay.getMonth() === dateMonth.getMonth(),
                date: getDateDay(firstDay),
                dayNumber: firstDay.getDate(),
                isSelected: selectedDays.includes(getDateDay(firstDay)),
            });

            firstDay.setDate(firstDay.getDate() + 1);
        } while (firstDay.getTime() <= lastDay.getTime());

        return days;

    }, [month, selected, lang]);

    if (!isMounted) {
        return <Loading />;
    }

    return <>
        {/* TODO <label>
            <input type="checkbox" />
            první den v týdnu je pondělí
        </label> */}
        <div className="calendar-header">
            <a onClick={() => {
                const date = new Date(month);
                date.setMonth(date.getMonth() - 1);
                setMonth(getDateDay(date));
            }}>
                <Icon path={mdiChevronLeft} size={1} />
            </a>

            {plural(dict.month, new Date(month).getMonth() + 1)}
            {" "}
            {new Date(month).getFullYear()}
            <a onClick={() => {
                const date = new Date(month);
                date.setMonth(date.getMonth() + 1);
                setMonth(getDateDay(date));
            }}>
                <Icon path={mdiChevronRight} size={1} />
            </a>

        </div>
        <div className="calendar">
            {days.filter((_, i) => i < 7).map(day => <div key={day.date} className="th">{dict.daysOfWeekShort[new Date(day.date).getDay()]}</div>)}
            {days.map(day => <div
                key={day.date}
                className={`td ${day.isInMonth ? "" : "out-month"} ${day.isSelected ? "selected" : ""} ${day.date === getDateDay() ? "today" : ""}`}
                onClick={() => onSelect(day.date)}
            >
                {day.dayNumber}
            </div>)}
        </div>
    </>;
}

function getDateDay(date: string | Date = new Date()): DateDay {
    if (typeof date === "string")
        date = new Date(date);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

function getDateTime(date: string | Date = new Date()): DateTime {
    if (typeof date === "string")
        date = new Date(date);

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${getDateDay(date)}T${hours}:${minutes}`;
}